import React, { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { EditOutlinedIcon, VisibilityOutlinedIcon } from 'tuui';
import { useNavigate } from 'hooks';
import {
    FEATURE_FLAGS,
    useAccount,
    useConfigMatch,
} from 'modules/taboola-common-frontend-modules/account-configurations';
import { FormattedMessage } from 'modules/taboola-common-frontend-modules/i18n';
import { CONVERSION_STATUS } from 'modules/tracking/config/constants';
import { generateConversionEditorPath, generateEventManagerPath } from 'modules/tracking/utils/generateTrackingPath';
import ActionsCellRenderer from './ActionsCellRenderer';

const ArchiveConversionTooltipContent = ({ isConversionArchived, isAnotherAccountConversion }) => {
    const messageId = isConversionArchived ? 'unarchive' : 'archive';
    return isAnotherAccountConversion ? (
        <FormattedMessage
            id="tracking.conversion.actionButtons.archive.otherPublisher"
            defaultMessage="Conversion of other accounts cannot be archived"
        />
    ) : (
        <FormattedMessage
            id={`tracking.conversion.actionButtons.${messageId}`}
            defaultMessage="Unarchived Conversion"
        />
    );
};

const EditConversionTooltipContent = ({ isAnotherAccountConversion }) =>
    isAnotherAccountConversion ? (
        <FormattedMessage
            id="tracking.conversion.actionButtons.edit.otherPublisher"
            defaultMessage="Conversion of other accounts cannot be edited"
        />
    ) : (
        <FormattedMessage id="tracking.conversion.actionButtons.edit" defaultMessage="Edit Conversion" />
    );

const PreviewConversionTooltipContent = () => (
    <FormattedMessage id="tracking.conversion.actionButtons.preview" defaultMessage="Preivew Conversion" />
);

export const ConversionActionsCellRenderer = ({ data, onConversionArchive, context, ...rest }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { accountName } = useAccount();
    const { accountName: currConversionAccountName, status } = data;
    const [isConversionArchived, setIsConversionArchived] = useState(status === CONVERSION_STATUS.ARCHIVED);
    const isDisabled = useMemo(
        () => accountName !== currConversionAccountName,
        [accountName, currConversionAccountName]
    );

    const isEventManagerEnabled = useConfigMatch({ [FEATURE_FLAGS.EVENT_MANAGER_ENABLED]: 'true' });

    const buttonsConfig = useMemo(() => {
        const buttons = [];
        if (isEventManagerEnabled) {
            buttons.push({
                onClick: () => {
                    navigate(generateEventManagerPath(currConversionAccountName, data.id));
                },
                isDisabled,
                dataMetricComponent: 'PreviewActionButton',
                icon: <VisibilityOutlinedIcon />,
                iconLabel: 'preview',
                tooltipContent: <PreviewConversionTooltipContent />,
            });
        }

        return [
            ...buttons,
            ...[
                {
                    onClick: () => {
                        navigate(generateConversionEditorPath(currConversionAccountName, data.id));
                    },
                    isDisabled,
                    dataMetricComponent: 'EditActionButton',
                    icon: <EditOutlinedIcon />,
                    iconLabel: 'edit',
                    tooltipContent: <EditConversionTooltipContent isAnotherAccountConversion={isDisabled} />,
                },
                {
                    onClick: () => {
                        const conversion = {
                            id: data.id,
                            status: isConversionArchived ? CONVERSION_STATUS.DISABLED : CONVERSION_STATUS.ARCHIVED,
                        };
                        setIsConversionArchived(prevState => !prevState);
                        dispatch(
                            onConversionArchive({
                                accountId: currConversionAccountName,
                                conversion,
                            })
                        );
                    },
                    isDisabled,
                    dataMetricComponent: 'archiveConversionActionButton',
                    iconLabel: isConversionArchived ? 'unarchive' : 'archive',
                    tooltipContent: (
                        <ArchiveConversionTooltipContent
                            isConversionArchived={isConversionArchived}
                            isAnotherAccountConversion={isDisabled}
                        />
                    ),
                },
            ],
        ];
    }, [
        currConversionAccountName,
        data.id,
        dispatch,
        isConversionArchived,
        isDisabled,
        isEventManagerEnabled,
        navigate,
        onConversionArchive,
    ]);

    return <ActionsCellRenderer data={data} buttonConfigs={buttonsConfig} context={context} {...rest} />;
};

ConversionActionsCellRenderer.propTypes = {
    /** row data which is injected by the grid */
    data: PropTypes.object,
    onEdit: PropTypes.func,
    onTogglePolicyState: PropTypes.func,
    ...ActionsCellRenderer.propTypes,
};
